import clsx from 'clsx'
import { Bar, BarChart, CartesianGrid, Cell, Customized, ReferenceLine, XAxis, YAxis } from 'recharts'
import { ChartContainer, ChartLegendContent, ChartTooltip } from '@/components/shadcn/charts'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { getComponentHeights } from '../utils'
import {
  CATEGORY_LABEL_WRAP_WIDTH,
  calculateAngledAxisBandSize,
  calculateCategoryYAxisWidth,
  calculateVerticalTickAxisBandSize,
  computeSymmetricDomain,
  formatSignedValue,
  generateSymmetricTicks,
  getBarColor,
  getBarRadius,
  measureChartTextWidth,
  wrapCategoryLabelLines,
} from './divergingBarChartUtils'
import {
  ChartDivergingBarDataSchema,
  ChartDivergingBarUISchema,
  DivergingBarDataItem,
  DivergingBarTooltipConfig,
  DivergingBarTooltipField,
} from './types'
import { normalizeRechartsData, remapRechartsRecordKeys } from '../shared/rechartsDataUtils'

const FIXED_SIZE_PER_CATEGORY = 60
const CHART_PADDING = 80
const TICK_LINE_HEIGHT = 16
const MIN_BAR_LENGTH_FOR_LABEL = 28

interface DivergingBarTooltipProps {
  active?: boolean
  payload?: Array<{
    payload: Record<string, unknown>
  }>
  categoryKey: string
  valueKey: string
  valueUnit?: string
  tooltip?: DivergingBarTooltipConfig
  decimalPlaces: number
  showSignInValueLabels: boolean
}

function DivergingBarTooltipContent({
  active,
  payload,
  categoryKey,
  valueKey,
  valueUnit,
  tooltip,
  decimalPlaces,
  showSignInValueLabels,
}: DivergingBarTooltipProps) {
  if (!active || !payload?.length) {
    return null
  }

  const data = payload[0].payload

  const fields: DivergingBarTooltipField[] =
    tooltip?.fields && tooltip.fields.length > 0
      ? tooltip.fields
      : [
          { dataKey: categoryKey, label: 'Category' },
          { dataKey: valueKey, label: 'Value', unit: valueUnit },
        ]

  return (
    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-offwhite px-2.5 py-1.5 text-xs shadow-xl">
      <div className="grid gap-1">
        {fields.map((field) => {
          const value = data[field.dataKey]
          if (value === undefined) return null

          const displayValue =
            field.dataKey === valueKey && typeof value === 'number'
              ? formatSignedValue(value, {
                  decimalPlaces,
                  showSign: showSignInValueLabels,
                  unit: field.unit,
                })
              : String(value)

          return (
            <div key={field.dataKey} className="flex justify-between gap-4">
              <span className="text-muted-foreground">{field.label}</span>
              <span className="font-mono font-medium tabular-nums text-foreground">
                {displayValue}
                {field.dataKey !== valueKey && field.unit && (
                  <span className="ml-0.5 text-muted-foreground">{field.unit}</span>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface DivergingBarValueLabelsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  xAxisMap?: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisMap?: Record<string, any>
  data: DivergingBarDataItem[]
  categoryKey: string
  valueKey: string
  isHorizontal: boolean
  valueUnit?: string
  decimalPlaces: number
  showSignInValueLabels: boolean
}

function DivergingBarValueLabels({
  xAxisMap,
  yAxisMap,
  data,
  categoryKey,
  valueKey,
  isHorizontal,
  valueUnit,
  decimalPlaces,
  showSignInValueLabels,
}: DivergingBarValueLabelsProps) {
  if (!xAxisMap || !yAxisMap) return null

  const xAxis = Object.values(xAxisMap)[0]
  const yAxis = Object.values(yAxisMap)[0]
  if (!xAxis || !yAxis) return null

  const categoryAxis = isHorizontal ? yAxis : xAxis
  const valueAxis = isHorizontal ? xAxis : yAxis
  const zeroPos = valueAxis.scale(0)
  const bandwidth = categoryAxis.bandSize || categoryAxis.bandwidth || 20
  const barCenterOffset = bandwidth / 2

  return (
    <g className="diverging-bar-value-labels">
      {data.map((row, index) => {
        const categoryPos = categoryAxis.scale(row[categoryKey])
        if (categoryPos === undefined) return null

        const value = Number(row[valueKey])
        if (Number.isNaN(value)) return null

        const valuePos = valueAxis.scale(value)
        const barLength = Math.abs(valuePos - zeroPos)
        if (barLength < MIN_BAR_LENGTH_FOR_LABEL) return null

        const label = formatSignedValue(value, {
          decimalPlaces,
          showSign: showSignInValueLabels,
          unit: valueUnit,
        })

        if (isHorizontal) {
          const y = categoryPos + barCenterOffset
          const isNegative = value < 0
          const x = isNegative ? Math.min(valuePos, zeroPos) - 8 : Math.max(valuePos, zeroPos) + 8
          return (
            <text
              key={`diverging-label-${index}`}
              x={x}
              y={y}
              textAnchor={isNegative ? 'end' : 'start'}
              dominantBaseline="central"
              fontSize={11}
              fill="hsl(var(--foreground))"
            >
              {label}
            </text>
          )
        }

        const x = categoryPos + barCenterOffset
        const isNegative = value < 0
        const y = isNegative ? Math.max(valuePos, zeroPos) + 14 : Math.min(valuePos, zeroPos) - 8
        return (
          <text
            key={`diverging-label-${index}`}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline={isNegative ? 'hanging' : 'auto'}
            fontSize={11}
            fill="hsl(var(--foreground))"
          >
            {label}
          </text>
        )
      })}
    </g>
  )
}

export function ChartDivergingBarContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: ChartDivergingBarUISchema
  dataSchema: ChartDivergingBarDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, chartConfig, emphasizeHeader } = uiSchema
  const {
    orientation,
    categoryKey,
    valueKey,
    colors,
    valueAxis,
    showGrid = true,
    showValueLabels = true,
    showSignInValueLabels = true,
    valueLabelDecimalPlaces = 2,
    hideAxis = false,
    fixedSize = false,
    lineClampCategoryLabels,
    xAxisOrientation,
    tooltip,
    config,
  } = chartConfig
  const { data } = dataSchema
  const { data: chartData, mapDataKey } = normalizeRechartsData(data, [
    categoryKey,
    valueKey,
    ...(tooltip?.fields?.map((field) => field.dataKey) ?? []),
  ])
  const normalizedCategoryKey = mapDataKey(categoryKey)
  const normalizedValueKey = mapDataKey(valueKey)
  const normalizedConfig = remapRechartsRecordKeys(config, mapDataKey)
  const normalizedTooltip = tooltip
    ? {
        ...tooltip,
        fields: tooltip.fields?.map((field) => ({
          ...field,
          dataKey: mapDataKey(field.dataKey),
        })),
      }
    : undefined
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  const isHorizontal = orientation === 'horizontal'
  const values = data.map((row) => Number(row[valueKey])).filter((value) => !Number.isNaN(value))
  const { domainMin, domainMax } = computeSymmetricDomain(values, valueAxis?.domain)
  const symmetricTicks = generateSymmetricTicks(domainMax)
  const tickMin = symmetricTicks[0] ?? domainMin
  const tickMax = symmetricTicks[symmetricTicks.length - 1] ?? domainMax
  const valueUnit = valueAxis?.unit

  const categoryLabels = data.map((item) => String(item[categoryKey] || ''))
  const categoryYAxisWidth = calculateCategoryYAxisWidth(categoryLabels, lineClampCategoryLabels)
  const categoryXAxisHeight = lineClampCategoryLabels
    ? calculateVerticalTickAxisBandSize(categoryLabels, lineClampCategoryLabels)
    : calculateAngledAxisBandSize(categoryLabels, 45)

  const valueTickLabels = symmetricTicks.map((tick) =>
    formatSignedValue(tick, {
      decimalPlaces: valueLabelDecimalPlaces,
      showSign: showSignInValueLabels,
      unit: valueUnit,
    })
  )
  const valueXAxisHeight = calculateAngledAxisBandSize(valueTickLabels, 45)
  const valueAxisTickWidth = Math.max(...valueTickLabels.map((label) => measureChartTextWidth(label)), 56) + 12

  const dataRowCount = data.length
  const fixedHeight = isHorizontal ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined
  const fixedWidth = !isHorizontal ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedYAxisTick = (props: any) => {
    const { x, y, payload } = props
    const lines = wrapCategoryLabelLines(String(payload.value), CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels!)

    return (
      <text x={x} y={y} fontSize={12} fill="hsl(var(--muted-foreground))">
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={x}
            dy={i === 0 ? -(lines.length - 1) * (TICK_LINE_HEIGHT / 2) : TICK_LINE_HEIGHT}
            textAnchor="end"
            dominantBaseline="central"
          >
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderAngledValueAxisTick = (props: any) => {
    const { x, y, payload } = props
    const text = formatSignedValue(Number(payload.value), {
      decimalPlaces: valueLabelDecimalPlaces,
      showSign: showSignInValueLabels,
      unit: valueUnit,
    })

    return (
      <text
        x={x}
        y={y}
        fontSize={12}
        fill="hsl(var(--muted-foreground))"
        transform={`rotate(-45, ${x}, ${y})`}
        textAnchor="end"
        dy={8}
      >
        {text}
      </text>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedXAxisTick = (props: any) => {
    const { x, y, payload } = props
    const lines = wrapCategoryLabelLines(String(payload.value), CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels!)

    return (
      <text
        x={x}
        y={y}
        fontSize={12}
        fill="hsl(var(--muted-foreground))"
        transform={`rotate(-90, ${x}, ${y})`}
        textAnchor="end"
      >
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : TICK_LINE_HEIGHT} dominantBaseline="central">
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  const legendEntry = config[valueKey]

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div
        className={clsx({
          'pt-5': hasHeader,
          'overflow-y-auto': fixedSize && isHorizontal,
          'overflow-x-auto': fixedSize && !isHorizontal,
        })}
        style={{ height: contentHeight }}
      >
        <ChartContainer
          className={clsx('mx-auto aspect-auto min-h-0 [&_.recharts-wrapper]:overflow-visible', {
            'h-full w-full': !fixedSize,
            'w-full': fixedSize && isHorizontal,
            'h-full': fixedSize && !isHorizontal,
          })}
          config={normalizedConfig}
          legend={
            legendEntry ? (
              <ChartLegendContent
                className="pt-3"
                items={[
                  { key: 'positive', label: `${legendEntry.label} (positive)`, color: colors.positive },
                  { key: 'negative', label: `${legendEntry.label} (negative)`, color: colors.negative },
                ]}
              />
            ) : undefined
          }
          style={{
            ...(fixedSize && isHorizontal && fixedHeight ? { height: fixedHeight, minHeight: fixedHeight } : {}),
            ...(fixedSize && !isHorizontal && fixedWidth ? { width: fixedWidth, minWidth: fixedWidth } : {}),
          }}
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            layout={isHorizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
            barGap={0}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}

            {isHorizontal ? (
              <>
                <YAxis
                  dataKey={normalizedCategoryKey}
                  type="category"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  width={categoryYAxisWidth}
                  interval={0}
                  {...(lineClampCategoryLabels ? { tick: renderClampedYAxisTick } : {})}
                />
                <XAxis
                  type="number"
                  hide={hideAxis}
                  domain={[tickMin, tickMax]}
                  ticks={symmetricTicks}
                  orientation={xAxisOrientation}
                  tick={renderAngledValueAxisTick}
                  interval={0}
                  height={valueXAxisHeight}
                />
                <ReferenceLine x={0} stroke="hsl(var(--border))" />
              </>
            ) : (
              <>
                <XAxis
                  dataKey={normalizedCategoryKey}
                  type="category"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  interval={0}
                  height={categoryXAxisHeight}
                  {...(lineClampCategoryLabels
                    ? { tick: renderClampedXAxisTick }
                    : {
                        angle: -45,
                        textAnchor: 'end' as const,
                      })}
                />
                <YAxis
                  type="number"
                  hide={hideAxis}
                  domain={[tickMin, tickMax]}
                  ticks={symmetricTicks}
                  width={valueAxisTickWidth}
                  tickMargin={8}
                  tickFormatter={(value: number) =>
                    formatSignedValue(value, {
                      decimalPlaces: valueLabelDecimalPlaces,
                      showSign: showSignInValueLabels,
                      unit: valueUnit,
                    })
                  }
                />
                <ReferenceLine y={0} stroke="hsl(var(--border))" />
              </>
            )}

            <ChartTooltip
              cursor={false}
              content={
                <DivergingBarTooltipContent
                  categoryKey={normalizedCategoryKey}
                  valueKey={normalizedValueKey}
                  valueUnit={valueUnit}
                  tooltip={normalizedTooltip}
                  decimalPlaces={valueLabelDecimalPlaces}
                  showSignInValueLabels={showSignInValueLabels}
                />
              }
            />

            <Bar
              dataKey={normalizedValueKey}
              isAnimationActive={false}
              barSize={24}
              radius={getBarRadius(0, isHorizontal)}
            >
              {data.map((row, index) => (
                <Cell
                  key={`diverging-cell-${index}`}
                  fill={getBarColor(Number(row[valueKey]), colors.positive, colors.negative)}
                />
              ))}
            </Bar>

            {showValueLabels && (
              <Customized
                component={(props: {
                  xAxisMap?: DivergingBarValueLabelsProps['xAxisMap']
                  yAxisMap?: DivergingBarValueLabelsProps['yAxisMap']
                }) => (
                  <DivergingBarValueLabels
                    xAxisMap={props.xAxisMap}
                    yAxisMap={props.yAxisMap}
                    data={data}
                    categoryKey={categoryKey}
                    valueKey={valueKey}
                    isHorizontal={isHorizontal}
                    valueUnit={valueUnit}
                    decimalPlaces={valueLabelDecimalPlaces}
                    showSignInValueLabels={showSignInValueLabels}
                  />
                )}
              />
            )}
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
